import { RecaptchaActions } from './actions'
// Extend the Window type to include grecaptcha.
declare global {
  interface Window {
    grecaptcha: {
      enterprise: {
        execute: (clientKey: string, options: { action: string }) => Promise<string>
      }
    }
  }
}

function getRecaptchaClientKey() {
  return process.env.EXPO_PUBLIC_GOOGLE_RECAPTCHA_WEB_CLIENT_KEY
}

// Helper to load the reCAPTCHA enterprise script on web if needed.
async function loadRecaptchaScript(clientKey: string): Promise<void> {
  if (!document.getElementById('recaptcha-enterprise-script')) {
    hideRecaptchaBadge()
    return new Promise<void>((resolve, reject) => {
      const script = document.createElement('script')
      script.id = 'recaptcha-enterprise-script'
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${clientKey}`
      script.async = true
      script.defer = true
      script.onload = () => {
        resolve()
      }
      script.onerror = () => reject(new Error('Failed to load reCAPTCHA enterprise script'))
      document.body.appendChild(script)
    })
  }
}

// Helper function to hide the reCAPTCHA badge by injecting CSS.
function hideRecaptchaBadge() {
  const style = document.createElement('style')
  style.innerHTML = `.grecaptcha-badge { visibility: hidden !important }`
  document.head.appendChild(style)
}

// Helper to poll until grecaptcha.enterprise is available.
export async function waitForGrecaptcha(timeout = 5000): Promise<void> {
  const start = Date.now()
  return new Promise((resolve, reject) => {
    const interval = setInterval(() => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (window.grecaptcha && window.grecaptcha.enterprise) {
        clearInterval(interval)
        resolve()
      } else if (Date.now() - start > timeout) {
        clearInterval(interval)
        reject(new Error('reCAPTCHA enterprise did not load in time.'))
      }
    }, 100)
  })
}

// On web, pre-load the reCAPTCHA script as soon as possible so that it loads immediately.

const clientKey = getRecaptchaClientKey()
if (!clientKey) {
  throw new Error('Missing reCAPTCHA client key configuration.')
}
const recaptchaScriptLoaded = loadRecaptchaScript(clientKey)

// Generic function that wraps a submission with reCAPTCHA verification.
export async function withRecaptchaVerification(action: RecaptchaActions): Promise<string> {
  const clientKey = getRecaptchaClientKey()
  if (!clientKey) {
    throw new Error('Missing reCAPTCHA client key configuration.')
  }

  await recaptchaScriptLoaded
  // Ensure grecaptcha.enterprise is available.
  await waitForGrecaptcha()
  return await window.grecaptcha.enterprise.execute(clientKey, { action })
}
